import { FaStar } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import { Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import UserImg from "../../../assets/home/user.jpg";

const getAgentReviewStats = (agent) => {
  const reviews = Array.isArray(agent?.reviews) ? agent.reviews : [];
  if (reviews.length === 0) {
    return { average: 0, count: 0 };
  }
  const starSum = reviews.reduce(
    (sum, item) => sum + Number(item?.star ?? item?.rating ?? 0),
    0,
  );
  return {
    average: Math.round((starSum / reviews.length) * 100) / 100,
    count: reviews.length,
  };
};

function HeroAgentCardSkeleton() {
  return (
    <div className="w-[292px] sm:w-[320px] rounded-[20px] overflow-hidden bg-white shadow-[0_12px_32px_rgba(15,23,42,0.1)] animate-pulse">
      <div className="h-[84px] bg-gray-200" />
      <div className="px-8 pt-6 pb-8 flex flex-col items-center">
        <div className="w-[124px] h-[124px] rounded-full bg-gray-200" />
        <div className="h-6 bg-gray-200 w-44 mt-6 rounded" />
        <div className="h-4 bg-gray-200 w-28 mt-4 rounded" />
        <div className="h-12 bg-gray-200 w-full mt-8 rounded-full" />
        <div className="h-12 bg-gray-200 w-full mt-3 rounded-full" />
        <div className="h-12 bg-gray-200 w-full mt-3 rounded-full" />
      </div>
    </div>
  );
}

export default function HeroAgentCard({ agent, loading }) {
  const navigate = useNavigate();

  if (loading) return <HeroAgentCardSkeleton />;
  if (!agent) return null;

  const headerBg =
    agent?.primaryColor ?? agent?.primary_color ?? "#097FE6";
  const displayName = agent?.name?.trim() || "Agent";
  const { average, count: reviewCount } = getAgentReviewStats(agent);
  const agentId = agent?._id ?? agent?.id;
  const phone = agent?.mobile_number ?? agent?.phone ?? agent?.contact_number;

  return (
    <div className="w-[250px] lg:w-[280px] rounded-[20px] overflow-hidden bg-white shadow-[0_12px_32px_rgba(15,23,42,0.1)]">
      <div
        className="h-[40px] lg:h-[60px] flex items-center justify-center px-6 cursor-pointer"
        style={{ backgroundColor: headerBg }}
        onClick={() => {
          if (agent?.agency_id) navigate(`/agency-profile/${agent.agency_id}`);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && agent?.agency_id) {
            navigate(`/agency-profile/${agent.agency_id}`);
          }
        }}
        role={agent?.agency_id ? "button" : undefined}
        tabIndex={agent?.agency_id ? 0 : undefined}
      >
        {agent?.agencyLogo ? (
          <img
            src={agent.agencyLogo}
            alt={agent?.agencyName || "Agency"}
            className="h-6 lg:h-8 max-w-[88%] object-contain"
          />
        ) : (
          <span className="text-white text-sm font-semibold tracking-wide truncate">
            {agent?.agencyName || "Agency"}
          </span>
        )}
      </div>

      <div className="p-4 lg:px-8 lg:pt-6 lg:pb-8 flex flex-col items-center">
        <div className="relative shrink-0">
          <img
            src={agent?.profileImg || UserImg}
            alt={displayName}
            className="w-[80px] lg:w-[100px] h-[80px] lg:h-[100px] rounded-full border-[3px] border-white ring-1 ring-[#E9E9E9] object-cover shadow-sm"
          />
          <div className="absolute -bottom-1 -right-1 bg-[#097FE6] w-7 h-7 rounded-full flex items-center justify-center border-[2px] border-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h3 className="mt-4 lg:mt-6 text-xl font-bold text-[#000929] text-center capitalize leading-tight">
          {displayName}
        </h3>

        <div className="flex flex-col items-center mt-3 w-full">
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                className={`text-xl ${star <= Math.round(average) ? "text-[#C2C823]" : "text-[#E5E7EB]"}`}
                aria-hidden
              />
            ))}
          </div>
          <p className="text-sm text-[#6B7280] mt-2.5">
            {reviewCount > 0
              ? `${reviewCount} ${reviewCount === 1 ? "review" : "reviews"}`
              : "No reviews yet"}
          </p>
        </div>

        <div className="mt-4 lg:mt-5 flex flex-col gap-3 w-full">
          {agentId ? (
            <button
              type="button"
              onClick={() => navigate(`/appraisal/${agentId}`)}
              className="w-full bg-[#097FE6] text-white py-3 px-4 rounded-full text-sm font-semibold hover:opacity-95 transition-opacity"
            >
              Request a free appraisal
            </button>
          ) : (
            <button
              type="button"
              disabled
              className="w-full bg-[#097FE6]/50 text-white py-3 px-4 rounded-full text-sm font-semibold cursor-not-allowed"
            >
              Request a free appraisal
            </button>
          )}

          {phone ? (
            <a
              href={`tel:${phone}`}
              className="w-full border-[1.5px] border-[#EAEAEA] py-3 px-4 rounded-full text-sm font-medium flex items-center justify-center gap-2.5 text-[#000929] hover:bg-[#F9FAFB] transition-colors"
            >
              <Phone size={18} strokeWidth={2} />
              <span>Call</span>
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="w-full border-[1.5px] border-[#EAEAEA] py-3 px-4 rounded-full text-sm font-medium flex items-center justify-center gap-2.5 text-[#9CA3AF] cursor-not-allowed"
            >
              <Phone size={18} strokeWidth={2} />
              <span>Call</span>
            </button>
          )}

          {agentId ? (
            <Link
              to={`/agent-profile/${agentId}`}
              className="w-full border-[1.5px] border-[#EAEAEA] py-3 px-4 rounded-full text-sm font-medium flex items-center justify-center gap-2.5 text-[#000929] hover:bg-[#F9FAFB] transition-colors"
            >
              <MdOutlineEmail className="w-[18px] h-[18px]" />
              <span>Enquire</span>
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className="w-full border-[1.5px] border-[#EAEAEA] py-3 px-4 rounded-full text-sm font-medium flex items-center justify-center gap-2.5 text-[#9CA3AF] cursor-not-allowed"
            >
              <MdOutlineEmail className="w-[18px] h-[18px]" />
              <span>Enquire</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
